// src/pages/OBDPage.js
/**
 * OBDPage.js
 *
 * This component provides a user interface for looking up OBD2 (On-Board Diagnostics) codes.
 * Users can enter a diagnostic trouble code (DTC), submit it, and receive details such as
 * code description and related system. The code fetches this data from a backend API.
 */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  Tooltip,
  IconButton,
  Grid,
} from "@mui/material";
import { Link } from "react-router-dom";
import { animate, stagger } from "motion";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import Header from "../components/header.js";
import Footer from "../components/footer.js";
import getApiBase from "../components/getApiBase";

const OBDPage = () => {
  const [code, setCode] = useState("");
  const [obdResult, setObdResult] = useState(null);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [userCars, setUserCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);

  /**
   * handleCheckCode
   *
   * Sends a request to the OBD2 API using the entered code and updates
   * the UI based on the response.
   *
   * - If the code field is empty, no request is made.
   * - On success: displays code details returned from the API.
   * - On failure: shows an appropriate error message.
   */

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (["User"].includes(storedUser?.role)) {
      setUser(storedUser);
      fetchUserCars(storedUser.id);
    }
  }, []);

  const fetchUserCars = async (userId) => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/user/cars?userId=${userId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      const data = await res.json();

      if (Array.isArray(data)) {
        setUserCars(data);
      } else {
        console.error("🚫 Invalid car data:", data);
        setUserCars([]); // fallback to avoid crash
      }
    } catch (err) {
      console.error("❌ Failed to fetch user cars:", err);
      setUserCars([]); // fallback to avoid crash
    }
  };

  const handleCheckCode = async () => {
    if (!code) return;

    setError("");
    setObdResult(null);
    setShowSavePrompt(false);

    try {
      const res = await fetch(`${getApiBase()}/api/obdcodes?code=${code}`);
      const data = await res.json();

      if (res.ok && data && data.code) {
        setObdResult(data);
        if (user) setShowSavePrompt(true);
      } else {
        setError("No details found for this OBD2 code.");
      }
    } catch (err) {
      console.error("❌ Error from frontend fetch:", err);
      setError("Error fetching OBD2 code details.");
    }
  };

  const handleSaveSearch = async () => {
    if (!selectedCar || !obdResult) return;

    try {
      //console.log("OBD2 codes", obdResult);
      const res = await fetch(`${getApiBase()}/api/obdcodes/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          carId: selectedCar,
          obdCodes: [obdResult.code],
          isActive,
          found_where: "searched",
        }),
      });

      if (res.ok) {
        alert("OBD2 code saved successfully.");
        setShowSavePrompt(false);
      } else {
        alert("Failed to save the OBD2 code.");
      }
    } catch (err) {
      console.error("Error saving code:", err);
      alert("Something went wrong. Please try again.");
    }
  };

  // ANIMATIONS
  useEffect(() => {
    const obdCard = Array.from(document.getElementsByClassName("obdCard"));
    animate(obdCard, { x: [-30, 0], opacity: [0, 1] }, { delay: stagger(0.3) });
  }, []);

  return (
    <Box sx={{ bgcolor: "#f7f9fc", minHeight: "100vh" }}>
      <Header />
      {/* Main 2 Column Layout */}
      <Grid
        container
        sx={{
          justifyContent: {
            xs: "center",
            md: "space-around",
          },
          alignItems: {
            xs: "center",
            md: "start",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          p: 5,
        }}
      >
        {/* LEFT COLUMN */}
        <Grid item size={{ xs: 12, md: 5 }} mb={{ xs: 4, md: 0 }}>
          <Card className="obdCard" elevation={3}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={1}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.4rem", sm: "1.8rem" },
                  }}
                >
                  🔧 OBD2 Code Lookup
                </Typography>

                <Tooltip
                  title="OBD2 (On-Board Diagnostics II) codes are standardized codes used by vehicle onboard computers to identify and report issues. For example, code P0171 means 'System Too Lean'."
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ padding: 0 }}>
                    <InfoOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <TextField
                label="Enter OBD2 Code (e.g., P0100)"
                fullWidth
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                sx={{ mb: 2, mt: 2 }}
              />

              <Button
                variant="contained"
                fullWidth
                onClick={handleCheckCode}
                sx={{ py: 1.2 }}
              >
                Check Code
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Descriptions are supplied by a third-party provider. Accuracy is not Autoshops’ responsibility. If a description looks wrong,{" "}
                <Link className="link" to="/contact?subject=Inaccurate%20diagnostic%20code">send a message</Link>.
              </Typography>

              {obdResult && (
                <Box mt={4}>
                  <Typography>
                    <strong>Code:</strong> {obdResult.code}
                  </Typography>
                  <Typography>
                    <strong>Description:</strong> {obdResult.description}
                  </Typography>
                </Box>
              )}

              {showSavePrompt && (
                <Box mt={4}>
                  <Typography>
                    <strong>Do you want to save this search?</strong>
                  </Typography>
                  <TextField
                    select
                    label="Select Your Car"
                    fullWidth
                    value={selectedCar || ""}
                    onChange={(e) => setSelectedCar(e.target.value)}
                    sx={{ mt: 2 }}
                  >
                    {userCars.map((car) => (
                      <MenuItem key={car.id} value={car.id}>
                        {car.year} {car.carMake} {car.carModel} (
                        {car.vin ? car.vin : "VIN Unknown"})
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box mt={2}>
                    <Button
                      variant={isActive ? "contained" : "outlined"}
                      onClick={() => setIsActive(!isActive)}
                    >
                      {isActive ? "✅ Code is Active" : "Mark Code as Active"}
                    </Button>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={handleSaveSearch}
                  >
                    Save Search
                  </Button>
                </Box>
              )}

              {error && (
                <Typography color="error" mt={3} align="center">
                  {error}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={{ sm: 12, md: 6 }} mb={{ xs: 4, md: 0 }}>
          <Card className="obdCard" elevation={3}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                gap={1}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.4rem", sm: "1.8rem" },
                  }}
                >
                  How To Find Your <span className="link">OBD2 Code</span>
                </Typography>
                <Typography
                  variant="body"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.2rem", sm: "1.4rem" },
                    width: "100%",
                    px: "1.0rem",
                  }}
                >
                  <Box display={"flex"} flexDirection={"column"} gap={4}>
                    <div>
                      An On-Board Diagnostics (OBD) code can be found in
                      vehicles made in 1996 or later with a device called an{" "}
                      <a
                        target="_blank"
                        href="https://www.google.com/search?q=OBD2+Scanner&sca_esv=cb1934bd945b0a8e&source=hp&ei=pTVYaIWUI4ORwbkPzojU0Qg&iflsig=AOw8s4IAAAAAaFhDtfn92YvJUsO8JoRlS4tsCGmR_7-W&ved=0ahUKEwjF-tbEv4WOAxWDSDABHU4ENYoQ4dUDCA8&uact=5&oq=OBD2+Scanner&gs_lp=Egdnd3Mtd2l6IgxPQkQyIFNjYW5uZXIyCBAAGIAEGLEDMgUQABiABDIFEAAYgAQyCBAAGIAEGLEDMgUQABiABDIFEAAYgAQyBRAAGIAEMgUQABiABDIFEAAYgAQyBRAAGIAESP4gUABYzh9wAHgAkAEAmAFjoAGFB6oBAjEyuAEDyAEA-AEBmAIMoAKjB8ICCxAAGIAEGJECGIoFwgIOEC4YgAQYsQMY0QMYxwHCAgUQLhiABMICCxAuGIAEGMcBGK8BwgIKEAAYgAQYQxiKBcICDRAAGIAEGLEDGEMYigXCAhAQABiABBixAxhDGIMBGIoFwgIOEAAYgAQYsQMYgwEYigWYAwCSBwQxMS4xoAf6RrIHBDExLjG4B6MHwgcGMC4xMC4yyAcZ&sclient=gws-wiz"
                        className="link"
                      >
                        OBD2 scanner
                      </a>
                      . The scanner is plugged into a port normally located
                      under the dashboard and steering wheel. This port is
                      connected to the Engine Control Module (ECM) in your car
                      that records specific information about the performance of
                      differrent componenets like the engine, transmission, and
                      emisions.
                    </div>
                    <div>
                      Many auto retail stores like{" "}
                      <span className="red">AutoZone</span> and{" "}
                      <span className="red">O'Reilly's Auto Parts</span> will
                      scan your car for free.
                    </div>
                    <div>
                      The <span className="link">OBD2 Code</span> is 5 digits,
                      each digit references specific information. They always
                      begin with one of these 4 letters that indicate the
                      following:
                    </div>
                    <div>
                      <span className="red">P</span> - Powertrain (engine,
                      transmission, etc.)
                    </div>
                    <div>
                      <span className="red">B</span> - Body (airbags, seatbelts,
                      etc.)
                    </div>
                    <div>
                      <span className="red">C</span> - Chassis (brakes,
                      suspension, etc.)
                    </div>
                    <div>
                      <span className="red">U</span> - Network (system
                      communications)
                    </div>
                  </Box>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Footer />
    </Box>
  );
};

export default OBDPage;
